import { type FC } from "react";
import { useForm } from "react-hook-form";
import Typography from "@mui/material/Typography";
import * as FormDialog from "../../shared/FormDialog";
import { useTranslation } from "@privacy/translations";
import { useCurrentOrganisation } from "../../root/CurrentOrganisationProvider";
import { useUserInfo } from "../../api/userInfo";
import { useMutation } from "@tanstack/react-query";
import { requestPartnerAssessmentMutationOptions } from "../../api/partners";
import { Stack } from "@mui/material";

type RequestAnswersDialogProps = {
  onClose: () => void;
  partnerName: string;
  complianceId: string;
  defaultEmail?: string;
};

export const RequestAssessmentDialog: FC<RequestAnswersDialogProps> = ({
  onClose,
  partnerName,
  complianceId,
  defaultEmail,
}) => {
  const { t } = useTranslation();
  const currentOrganisation = useCurrentOrganisation();
  const userInfo = useUserInfo();
  const senderName = `${userInfo.firstName} ${userInfo.lastName}`;
  const requestPartnerAssessmentMutation = useMutation(requestPartnerAssessmentMutationOptions({ complianceId }));

  const organisationName = currentOrganisation.name;
  const { handleSubmit, control, setFocus } = useForm<{ to: string; subject: string; message: string }>({
    defaultValues: {
      to: defaultEmail ?? "",
      subject: t("Compliance.Subdrawer.RequestAssessmentDialog.SubjectDefault", { senderName, organisationName }),
      message: t("Compliance.Subdrawer.RequestAssessmentDialog.MessageDefault", { senderName, organisationName }),
    },
  });

  FormDialog.useAutoFocusField(setFocus, defaultEmail ? null : "to");

  return (
    <FormDialog.Dialog isOpen={true} maxWidth="880px">
      <FormDialog.Title>{t("Compliance.Subdrawer.RequestAssessmentDialog.Title", { partnerName })}</FormDialog.Title>
      <FormDialog.Form
        onSubmit={handleSubmit(data =>
          requestPartnerAssessmentMutation.mutate(
            { ...data, to: defaultEmail ? undefined : data.to },
            { onSuccess: () => onClose() }
          )
        )}
      >
        <FormDialog.Content>
          <FormDialog.FormError isError={requestPartnerAssessmentMutation.isError} />
          <Typography
            sx={{
              pb: 2,
              fontSize: theme => theme.typography.pxToRem(14),
              color: theme => theme.palette.text.secondary,
            }}
          >
            {t("Compliance.Subdrawer.RequestAssessmentDialog.Description")}
          </Typography>
          <FormDialog.Fields>
            <FormDialog.TextField
              control={control}
              name="to"
              rules={{ required: t("Input.Validation.RequiredEmail") }}
              label={t("Compliance.Subdrawer.RequestAssessmentDialog.ToLabel")}
              disabled={Boolean(defaultEmail)}
              placeholder={t("Compliance.Subdrawer.RequestAssessmentDialog.ToPlaceholder")}
            />

            <Stack>
              <Typography
                sx={theme => ({ fontSize: theme.typography.pxToRem(14), color: theme.palette.text.tertiary })}
              >
                {t("Compliance.Subdrawer.RequestAssessmentDialog.FromLabel")}
              </Typography>

              <Typography
                sx={theme => ({ fontSize: theme.typography.pxToRem(14), color: theme.palette.text.tertiary })}
              >
                {t("Compliance.Subdrawer.RequestAssessmentDialog.FromValue", {
                  senderName,
                  organisationName,
                })}
              </Typography>
            </Stack>

            <FormDialog.TextField
              control={control}
              name="subject"
              rules={{ required: t("Input.Validation.Required") }}
              label={t("Compliance.Subdrawer.RequestAssessmentDialog.SubjectLabel")}
            />

            <FormDialog.TextField
              control={control}
              name="message"
              rules={{ required: t("Input.Validation.Required") }}
              label={t("Compliance.Subdrawer.RequestAssessmentDialog.MessageLabel")}
              multiline
              rows={8}
            />
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton isSubmitting={requestPartnerAssessmentMutation.isPending}>
            {t("Compliance.Subdrawer.RequestAssessmentDialog.SubmitButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
